<template>
  <div class="dashboard-filter-bar" :class="{ card: showsCard }">
    <!-- W9 §2. Shut, the bar is one line: the pencil, and - only when something is in force -
         the line that says so, because a filtered figure read as a company figure is the harm
         worth guarding against. The panel expands in place; it is never a Dialog, because a
         blocking modal is forbidden on this project. -->
    <!-- W7 tail. Shut with nothing to say, there is nothing to put in a card: the `card` chrome
         and the head's reserved row height would draw a full-size empty panel above the figures
         holding one pencil. Both come back the moment the panel opens or a summary appears. -->
    <div class="dashboard-bar-head" :class="{ 'dashboard-bar-head-bare': !showsCard }">
      <span v-if="summaryLine" class="dashboard-bar-saved">{{ summaryLine }}</span>
      <Button
        class="p-button-text dashboard-bar-pencil"
        icon="pi pi-pencil"
        title="Change what this dashboard shows"
        :aria-expanded="open ? 'true' : 'false'"
        @click="togglePanel"
      />
    </div>

    <div v-if="open" class="dashboard-bar-panel">
      <div class="p-grid p-fluid p-ai-end">
        <div
          v-for="dim in dimensionFields"
          :key="dim.key"
          class="p-col-12 p-md-6 p-lg-2"
        >
          <label class="dashboard-bar-label">{{ dim.label }}</label>
          <MultiSelect
            :class="'dashboard-bar-' + dim.key"
            :value="selection(dim.key)"
            :options="dim.options"
            optionLabel="label"
            optionValue="value"
            :filter="true"
            :disabled="dim.disabled"
            :title="dim.title"
            placeholder="Widget settings"
            @input="onDimensionInput(dim.key, $event)"
            @hide="commitPending"
          />
        </div>

        <div class="p-col-12 p-md-6 p-lg-2">
          <label class="dashboard-bar-label">Date</label>
          <Dropdown
            class="dashboard-bar-period"
            :value="periodSelection"
            :options="periodOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Widget settings"
            @input="onPeriodInput"
          />
        </div>

        <template v-if="periodSelection === 'custom'">
          <div class="p-col-6 p-md-3 p-lg-1">
            <label class="dashboard-bar-label">From</label>
            <Calendar
              class="dashboard-bar-from"
              :value="dateValue('dateFrom')"
              dateFormat="dd/mm/yy"
              @input="onDateInput('dateFrom', $event)"
            />
          </div>
          <div class="p-col-6 p-md-3 p-lg-1">
            <label class="dashboard-bar-label">To</label>
            <Calendar
              class="dashboard-bar-to"
              :value="dateValue('dateTo')"
              dateFormat="dd/mm/yy"
              @input="onDateInput('dateTo', $event)"
            />
          </div>
        </template>
      </div>

      <!-- Restricted, but with no branch to be restricted to: say so, rather than quietly
           showing everything (W9 §2.2; `noBranchHandle` is the precedent). -->
      <div v-if="restrictedWithoutBranch" class="dashboard-bar-no-branch">
        No default branch set. Please notify the administrator to allow you to access this
        report's data.
      </div>

      <div class="dashboard-bar-status">
        <template v-if="onSavedView">
          <span class="dashboard-bar-saved-detail">My saved view: {{ savedViewSummary }}</span>
        </template>
        <template v-else>
          <span v-if="!barIsEmpty" class="dashboard-bar-saved-detail">Filtered: {{ barSummary }}</span>
          <a v-if="hasSavedView" class="dashboard-bar-link" @click="useSavedView">Open my saved view</a>
        </template>
      </div>

      <div class="dashboard-bar-buttons">
        <Button
          v-if="canSave"
          class="p-button-text dashboard-bar-save"
          icon="pi pi-save"
          label="Save as my opening view"
          :disabled="saveDisabled"
          :title="saveTitle"
          @click="saveView"
        />
        <Button
          v-if="canSave && hasSavedView"
          class="p-button-text dashboard-bar-clear"
          icon="pi pi-trash"
          label="Remove my saved view"
          title="Remove my saved view"
          :disabled="saving"
          @click="clearView"
        />
        <Button
          class="p-button-text dashboard-bar-reset"
          icon="pi pi-refresh"
          label="Reset to my defaults"
          title="Put every widget back on its own stored settings"
          @click="resetToDefaults"
        />
        <Button
          class="p-button-text dashboard-bar-done"
          label="Done"
          title="Close this panel"
          @click="togglePanel"
        />
      </div>

      <!-- W9 §2.1.4: the same on/off list as the My Widgets screen, the same component and the
           same calls - not a second list. -->
      <!-- Only your own dashboard: every call MyWidgets makes is about the signed-in user, so
           on someone else's dashboard the list would be editing the wrong person's widgets. -->
      <div v-if="canSave" class="dashboard-bar-widgets">
        <MyWidgets @changed="onWidgetsChanged" />
      </div>
    </div>
  </div>
</template>

<script>
// The dashboard filter bar (item 4, decisions 2b, 2c, 5). It edits a bar (see
// dashboardFilterBar.js) and emits `input` with the new one; Dashboard.vue owns where the bar
// lives (route query + Vuex) and what gets reloaded. The saved view is read and written here
// and reported with `saved-view`.
import SettingService from '@/service/SettingService'
import CustomerService from '@/service/CustomerService'
import OpportunityService from '@/service/OpportunityService'
import FiltersService from '@/service/FiltersSerice'
import UserService from '@/service/UserService'
import MyWidgets from './MyWidgets.vue'
import {
  DIMENSIONS,
  emptyBar,
  barFromSavedView,
  savedViewBody,
  sameBar,
  isCustomRange,
  isEmptyBar,
  regionLabel,
  formatBarDate,
  parseBarDate,
  dimensionToSelection,
  selectionToDimension
} from './dashboardFilterBar'

const DIMENSION_LABELS = { branch: 'Branch', region: 'Region', salesExec: 'Sales Exec', prodType: 'Product Type' }

export default {
  components: { MyWidgets },
  props: {
    value: {
      type: Object,
      default: () => emptyBar()
    },
    // v1/dashboardSavedView as returned: { saved: false } or { saved: true, branch, ... }.
    savedView: {
      type: Object,
      default: null
    },
    // Only on the signed-in user's own dashboard: the saved view belongs to the caller.
    canSave: {
      type: Boolean,
      default: false
    },
    // W9 §2.2, worked out by Dashboard.vue: null, or { restricted, branch, branchId } when the
    // signed-in user holds a level-5 ("Restricted") permission behind one of their own widgets.
    restriction: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    settingService: new SettingService(),
    customerService: new CustomerService(),
    opportunityService: new OpportunityService(),
    filtersService: new FiltersService(),
    userService: new UserService(),
    branches: [],
    regions: [],
    salesExecs: [],
    productTypes: [],
    dateModifiers: [],
    // A dimension being edited while its panel is open; committed when the panel closes, so
    // ticking three branches reloads the dashboard once rather than three times.
    pending: {},
    customPicked: false,
    saving: false,
    // Item 4 follow-up (04/10/2026): always shut on arrival, including back from a drill. It
    // opens only from the pencil; the summary line says when a filter is in force.
    open: false
  }),
  created() {
    this.loadLookups()
  },
  computed: {
    dimensionFields() {
      const items = {
        branch: this.branches.map(b => ({ value: String(b.id), label: b.branchName || `Branch ${b.id}` })),
        region: this.regions.map(r => ({ value: String(r.id), label: regionLabel(r) })),
        salesExec: this.salesExecs.map(s => ({ value: String(s.id), label: s.salesExecName || `Sales exec ${s.id}` })),
        prodType: this.productTypes.map(p => ({ value: String(p.id), label: p.productTypeName || `Product type ${p.id}` }))
      }
      return DIMENSIONS.map(key => {
        // A restricted user's Branch control is their own branch and nothing else: disabled,
        // named, and saying why (W9 §2.2).
        if (key === 'branch' && this.restrictedBranch) {
          return {
            key,
            label: DIMENSION_LABELS[key],
            options: [{ value: this.restrictedBranch.branchId, label: this.restrictedBranchName }],
            disabled: true,
            title: `Your access is limited to ${this.restrictedBranchName}`
          }
        }
        return {
          key,
          label: DIMENSION_LABELS[key],
          options: [
            { value: '', label: 'Widget settings' },
            { value: '-1', label: 'All' },
            { value: '0', label: 'Own setting' },
            ...items[key]
          ],
          disabled: false,
          title: null
        }
      })
    },
    // W7 tail: the card is drawn only when it has something in it - the open panel, or the line
    // that says a filter is in force.
    showsCard() {
      return this.open || !!this.summaryLine
    },
    // Only the summary survives the panel being shut (W9 §2).
    summaryLine() {
      if (this.onSavedView) return `My saved view: ${this.savedViewSummary}`
      if (!this.barIsEmpty) return `Filtered: ${this.barSummary}`
      return ''
    },
    restrictedBranch() {
      return this.restriction && this.restriction.restricted && this.restriction.branchId
        ? this.restriction
        : null
    },
    restrictedWithoutBranch() {
      return !!(this.restriction && this.restriction.restricted && !this.restriction.branchId)
    },
    restrictedBranchName() {
      const branch = this.restrictedBranch && this.restrictedBranch.branch
      if (!branch) return ''
      return branch.branchName || `Branch ${branch.id}`
    },
    periodOptions() {
      const named = this.dateModifiers
        .filter(m => m && m.dateModifierFunc && m.dateModifierFunc !== 'custom' && m.active !== false)
        .map(m => ({ value: m.dateModifierFunc, label: m.dateModifierName || m.dateModifierFunc }))
      return [{ value: '', label: 'Widget settings' }, ...named, { value: 'custom', label: 'Custom range' }]
    },
    periodSelection() {
      if (this.customPicked || isCustomRange(this.value)) return 'custom'
      return (this.value && this.value.period) || ''
    },
    hasSavedView() {
      return !!(this.savedView && this.savedView.saved)
    },
    onSavedView() {
      return this.hasSavedView && sameBar(barFromSavedView(this.savedView), this.value)
    },
    barIsEmpty() {
      return isEmptyBar(this.value)
    },
    saveDisabled() {
      return !this.canSave || this.saving || this.periodSelection === 'custom' || this.barIsEmpty || this.onSavedView
    },
    saveTitle() {
      if (this.periodSelection === 'custom') return 'A saved view keeps a named period, not a custom range'
      if (this.onSavedView) return 'This is your saved view'
      return 'Save as my dashboard view'
    },
    savedViewSummary() {
      return this.describe(barFromSavedView(this.savedView))
    },
    barSummary() {
      return this.describe(this.value)
    }
  },
  watch: {
    value(val) {
      if (isCustomRange(val)) this.customPicked = false
    }
  },
  methods: {
    togglePanel() {
      this.open = !this.open
    },
    // W9 §2.1.3: the "Use widget settings" behaviour that exists today, as a named button that
    // says what it did.
    resetToDefaults() {
      this.useWidgetSettings()
      this.$toast.add({
        severity: 'success',
        summary: 'Reset',
        detail: 'Every widget is back to its own settings.',
        life: 3000
      })
    },
    onWidgetsChanged(entry) {
      this.$emit('widgets-changed', entry)
    },
    async loadLookups() {
      // All rows, not just active ones: widgets store inactive ids too, and region 5 is inactive.
      try { this.branches = (await this.settingService.getAllBranches()) || [] } catch (e) {}
      try { this.regions = (await this.settingService.getAllRegions()) || [] } catch (e) {}
      try { this.salesExecs = (await this.customerService.getAllSalesExecs()) || [] } catch (e) {}
      try { this.productTypes = (await this.opportunityService.getAllProductTypes()) || [] } catch (e) {}
      try { this.dateModifiers = (await this.filtersService.getDateModifiers()) || [] } catch (e) {}
    },
    selection(key) {
      // A restricted user's branch is their own, whatever the bar says it is.
      if (key === 'branch' && this.restrictedBranch) return [this.restrictedBranch.branchId]
      if (Object.prototype.hasOwnProperty.call(this.pending, key)) return dimensionToSelection(this.pending[key])
      return dimensionToSelection(this.value && this.value[key])
    },
    onDimensionInput(key, next) {
      const value = selectionToDimension(this.selection(key), next)
      this.pending = Object.assign({}, this.pending, { [key]: value })
    },
    commitPending() {
      const keys = Object.keys(this.pending)
      if (!keys.length) return
      const next = Object.assign({}, this.value, this.pending)
      this.pending = {}
      if (!sameBar(next, this.value)) this.$emit('input', next)
    },
    onPeriodInput(func) {
      if (func === 'custom') {
        this.customPicked = true
        // The named period is dropped now; the range applies once a date is picked.
        if (this.value.period) this.$emit('input', Object.assign({}, this.value, { period: '', dateFrom: '', dateTo: '' }))
        return
      }
      this.customPicked = false
      this.$emit('input', Object.assign({}, this.value, { period: func || '', dateFrom: '', dateTo: '' }))
    },
    dateValue(key) {
      return parseBarDate(this.value && this.value[key])
    },
    onDateInput(key, date) {
      this.$emit('input', Object.assign({}, this.value, { period: '', [key]: formatBarDate(date) }))
    },
    useWidgetSettings() {
      this.pending = {}
      this.customPicked = false
      this.$emit('input', emptyBar())
    },
    useSavedView() {
      const bar = barFromSavedView(this.savedView)
      if (!bar) return
      this.pending = {}
      this.customPicked = false
      this.$emit('input', bar)
    },
    async saveView() {
      if (this.saveDisabled) return
      this.saving = true
      try {
        const view = await this.userService.saveDashboardSavedView(savedViewBody(this.value))
        this.$emit('saved-view', view)
        this.$toast.add({ severity: 'success', summary: 'Saved', detail: 'This is now your dashboard view.', life: 3000 })
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: this.errorText(err, 'The view was not saved.'), life: 5000 })
      } finally {
        this.saving = false
      }
    },
    async clearView() {
      this.saving = true
      try {
        const view = await this.userService.clearDashboardSavedView()
        this.$emit('saved-view', view || { saved: false })
        this.$toast.add({ severity: 'success', summary: 'Removed', detail: 'Your saved dashboard view was removed.', life: 3000 })
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Error', detail: this.errorText(err, 'The saved view was not removed.'), life: 5000 })
      } finally {
        this.saving = false
      }
    },
    errorText(err, fallback) {
      const data = err && err.response && err.response.data
      return (data && (data.message || (typeof data === 'string' ? data : null))) || fallback
    },
    // "Branch: LONDON · Region: All · This Month" - ids fall back to their number until the
    // lookups arrive.
    describe(bar) {
      if (isEmptyBar(bar)) return 'widget settings'
      const parts = []
      this.dimensionFields.forEach(dim => {
        const value = bar[dim.key]
        if (!value) return
        const names = value.split(',').map(id => {
          const option = dim.options.find(o => o.value === id)
          return option ? option.label : id
        })
        parts.push(`${dim.label}: ${names.join(', ')}`)
      })
      if (bar.period) {
        const option = this.periodOptions.find(o => o.value === bar.period)
        parts.push(option ? option.label : bar.period)
      } else if (bar.dateFrom || bar.dateTo) {
        parts.push(`${bar.dateFrom || '…'} to ${bar.dateTo || '…'}`)
      }
      return parts.join(' · ')
    }
  }
}
</script>

<style scoped>
.dashboard-filter-bar {
  margin-bottom: 1rem;
}
.dashboard-bar-label {
  display: block;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}
.dashboard-bar-status {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  min-height: 2.5rem;
}
.dashboard-bar-link {
  cursor: pointer;
  text-decoration: underline;
}
.dashboard-bar-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  min-height: 2.5rem;
}
/* W7 tail: shut and silent, the head reserves no row - the pencil is the whole bar. */
.dashboard-bar-head-bare {
  min-height: 0;
}
/* The pencil sits at the top right; the summary, when there is one, keeps the left. */
.dashboard-bar-pencil {
  margin-left: auto;
}
/* An expanding card, not a dialog: nothing on the dashboard is ever blocked behind it. */
.dashboard-bar-panel {
  border-top: 1px solid #e0e0e0;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
}
.dashboard-bar-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.dashboard-bar-widgets {
  border-top: 1px solid #e0e0e0;
  margin-top: 0.75rem;
  padding-top: 0.5rem;
}
.dashboard-bar-no-branch {
  color: #b00020;
  margin: 0.5rem 0;
}
</style>
